import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    IconButton,
    Typography,
    Autocomplete,
    useMediaQuery,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { incrementActionCounter } from "../../utils/actionCounters";

const CommissionEditDialog = ({ open, onClose, policy, onUpdate, userId }) => {
    const { user } = useSelector((state) => state.auth);
    const isTinyScreen = useMediaQuery('(max-height:720px)');
    const isShortScreen = useMediaQuery('(max-height:900px)');
    const [formData, setFormData] = useState({
        tpPremium: "",
        odPremium: "",
        premiumAmount: "",
        netPremium: "",
        discount: "",
        paidAmount: "",
        commissionRate: "0", // Default
        tdsRate: "2",         // Default
        totalCommission: "",
        modeOfPayment: "",
        remarks: "",
    });

    const [paymentModes] = useState(["Cash", "UPI", "UPI+Cash", "Cheque", "Online Transfer", "Online Transfer+Cash"]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Reusable TextField styling (matching UnifiedPolicyForm)
    const textFieldStyles = {
        width: "100%",
        "& .MuiInputBase-root": {
            height: isTinyScreen ? "44px" : "56px",
            borderRadius: "8px",
            borderWidth: "1px",
            fontSize: "14px",
            backgroundColor: "#FFFFFF",
            "& fieldset": {
                borderColor: "#E0E0E0",
                borderWidth: "1px",
            },
            "&:hover fieldset": {
                borderColor: "#BDBDBD",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#0063F7",
                borderWidth: "2px",
            },
        },
        "& .MuiInputBase-input": {
            padding: isTinyScreen ? "10px 12px" : "16px 12px",
            height: isTinyScreen ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
        },
        "& .MuiInputLabel-root": {
            fontSize: "14px",
            color: "#757575",
            "&.Mui-focused": {
                fontSize: "14px",
                color: "#0063F7",
            },
        }
    };

    const autocompleteStyles = {
        width: "100%",
        "& .MuiInputBase-root": {
            height: isTinyScreen ? "44px" : "56px",
            borderRadius: "8px",
            borderWidth: "1px",
            fontSize: "14px",
            backgroundColor: "#FFFFFF",
            "& fieldset": {
                borderColor: "#E0E0E0",
                borderWidth: "1px",
            },
            "&:hover fieldset": {
                borderColor: "#BDBDBD",
            },
            "&.Mui-focused fieldset": {
                borderColor: "#0063F7",
                borderWidth: "2px",
            },
        },
        "& .MuiInputBase-input": {
            padding: isTinyScreen ? "10px 12px" : "16px 12px",
            height: isTinyScreen ? "20px" : "24px",
            display: "flex",
            alignItems: "center",
        },
        "& .MuiInputLabel-root": {
            fontSize: "14px",
            color: "#757575",
            "&.Mui-focused": {
                fontSize: "14px",
                color: "#0063F7",
            },
        }
    };


    useEffect(() => {
        setFormData({
            tpPremium: policy.tpPremium || "",
            odPremium: policy.odPremium || "",
            premiumAmount: policy.premiumAmount || "",
            netPremium: policy.netPremium || "",
            discount: policy.discount || "0",
            amountPayable: policy.amountPayable || policy.premiumAmount || "",
            paidAmount: policy.paidAmount || "",
            commissionRate: policy.commissionRate || "0",
            odCommissionRate: policy.odCommissionRate || "0",
            odCommissionAmount: policy.odCommissionAmount || "0.00",
            tpCommissionRate: policy.tpCommissionRate || "0",
            tpCommissionAmount: policy.tpCommissionAmount || "0.00",
            tdsRate: policy.tdsRate || "2",
            totalCommission: Math.max(0, parseFloat(policy.totalCommission || 0)).toFixed(2),
            modeOfPayment: policy.modeOfPayment || "",
            remarks: policy.remarks || "",
        });
    }, [policy]);

    // Auto-calculation logic
    useEffect(() => {
        const netPremium = parseFloat(formData.netPremium) || 0;
        const tds = parseFloat(formData.tdsRate) || 0;
        let grossCommission = 0;

        if (policy?.policyCategory === 'motor') {
            const odAmount = parseFloat(formData.odCommissionAmount) || 0;
            const tpAmount = parseFloat(formData.tpCommissionAmount) || 0;
            grossCommission = odAmount + tpAmount;
        } else {
            const rate = parseFloat(formData.commissionRate) || 0;
            grossCommission = netPremium * (rate / 100);
        }

        // Total Commission = Gross Commission * (1 - TDS%)
        const total = Math.max(0, grossCommission * (1 - (tds / 100)));

        setFormData(prev => ({
            ...prev,
            totalCommission: total.toFixed(2)
        }));
    }, [formData.netPremium, formData.commissionRate, formData.odCommissionAmount, formData.tpCommissionAmount, formData.tdsRate, policy?.policyCategory]);


    const handleChange = (e) => {
        let { name, value } = e.target;

        // Prevent negative values for amount and rate fields
        if (['tpPremium', 'odPremium', 'premiumAmount', 'netPremium', 'discount', 'paidAmount', 'commissionRate', 'odCommissionRate', 'tpCommissionRate', 'tdsRate', 'odCommissionAmount', 'tpCommissionAmount', 'totalCommission'].includes(name)) {
            // Remove any negative sign and other non-numeric chars except dot
            value = value.replace(/[^0-9.]/g, '');
        }
        setFormData(prev => {
            const newData = { ...prev, [name]: value };

            // Payment logic
            if (name === 'premiumAmount' || name === 'discount') {
                const total = parseFloat(newData.premiumAmount) || 0;
                const disc = parseFloat(newData.discount) || 0;
                newData.amountPayable = (total - disc).toFixed(2);
            }

            // Motor Commission logic
            if (policy?.policyCategory === 'motor') {
                const odPremium = parseFloat(newData.odPremium) || 0;
                const tpPremium = parseFloat(newData.tpPremium) || 0;

                if (name === 'odCommissionRate') {
                    const rate = parseFloat(value) || 0;
                    newData.odCommissionAmount = (odPremium * (rate / 100)).toFixed(2);
                } else if (name === 'odCommissionAmount') {
                    const amount = parseFloat(value) || 0;
                    newData.odCommissionRate = odPremium > 0 ? ((amount / odPremium) * 100).toFixed(2) : "0";
                } else if (name === 'tpCommissionRate') {
                    const rate = parseFloat(value) || 0;
                    newData.tpCommissionAmount = (tpPremium * (rate / 100)).toFixed(2);
                } else if (name === 'tpCommissionAmount') {
                    const amount = parseFloat(value) || 0;
                    newData.tpCommissionRate = tpPremium > 0 ? ((amount / tpPremium) * 100).toFixed(2) : "0";
                }
            }

            return newData;
        });
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        try {
            if (!userId) throw new Error("User ID missing");
            const docRef = doc(db, `agents/${userId}/policies/${policy.id}`);
            await updateDoc(docRef, {
                tpPremium: formData.tpPremium,
                odPremium: formData.odPremium,
                premiumAmount: formData.premiumAmount,
                totalAmount: formData.premiumAmount,
                netPremium: formData.netPremium,
                discount: formData.discount,
                amountPayable: formData.amountPayable,
                paidAmount: formData.paidAmount,
                receivedAmount: formData.paidAmount,
                commissionRate: formData.commissionRate,
                odCommissionRate: formData.odCommissionRate,
                odCommissionAmount: formData.odCommissionAmount,
                tpCommissionRate: formData.tpCommissionRate,
                tpCommissionAmount: formData.tpCommissionAmount,
                tdsRate: formData.tdsRate,
                totalCommission: formData.totalCommission,
                modeOfPayment: formData.modeOfPayment,
                remarks: formData.remarks,
                grossCommission: (policy?.policyCategory === 'motor') ?
                    (parseFloat(formData.odCommissionAmount) + parseFloat(formData.tpCommissionAmount)) :
                    ((parseFloat(formData.netPremium) * parseFloat(formData.commissionRate)) / 100)
            });

            // Increment commission edit counter for admin users
            if (user?.isPhoneUser) {
                await incrementActionCounter(user.uid, 'commsioneditcounter');
            }

            toast.success("Policy updated successfully!");
            if (onUpdate) onUpdate();
            onClose();
        } catch (err) {
            console.error(err);
            toast.error("Failed to update policy.");
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <div className="unified-modal-overlay"> {/* Re-using class if available globally, else style manually */}
            <div
                className="unified-modal-content"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: '1037px',
                    width: '100%',
                    maxHeight: '90vh',
                    height: 'auto',
                    borderRadius: '12px',
                    background: '#FFFFFF',
                    padding: '20px 32px',
                    gap: '24px', // Reduced gap slightly to account for internal spacing
                    position: 'relative',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
                    overflow: 'hidden', // Changed from overflowY: 'auto'
                    margin: 'auto'
                }}
            >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
                    <h2
                        style={{
                            margin: 0,
                            fontFamily: "Roboto Slab",
                            fontWeight: 500,
                            fontSize: "32px",
                            lineHeight: "56px",
                            letterSpacing: "0.02em",
                            color: "#212121",
                        }}
                    >
                        Edit Commission Details
                    </h2>
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </div>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '24px',
                    flex: 1,
                    overflowY: 'auto',
                    paddingRight: '8px', // Prevent scrollbar from overlapping content
                    marginRight: '-8px' // Compensate for padding
                }}>
                    {/* Payment Details */}
                    <h3 style={{ fontFamily: 'Roboto Slab', fontSize: '20px', fontWeight: 500, marginBottom: '8px' }}>Payment Details</h3>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px' }}>
                        {policy?.policyCategory === "motor" && (
                            <>
                                {policy.policyType !== "Liability" && (
                                    <TextField
                                        label="OD Premium"
                                        name="odPremium"
                                        value={formData.odPremium}
                                        onChange={handleChange}
                                        sx={textFieldStyles}
                                    />
                                )}
                                {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                                    <TextField
                                        label="TP Premium"
                                        name="tpPremium"
                                        value={formData.tpPremium}
                                        onChange={handleChange}
                                        sx={textFieldStyles}
                                    />
                                )}
                            </>
                        )}
                        <TextField
                            label="Net Premium"
                            name="netPremium"
                            value={formData.netPremium}
                            onChange={handleChange}
                            sx={textFieldStyles}
                        />
                        <TextField
                            label="Total Premium"
                            name="premiumAmount"
                            value={formData.premiumAmount}
                            onChange={handleChange}
                            sx={textFieldStyles}
                        />
                        <TextField
                            label="Customer Discount"
                            name="discount"
                            value={formData.discount}
                            onChange={handleChange}
                            sx={textFieldStyles}
                        />
                        <TextField
                            label="Amount Payable"
                            name="amountPayable"
                            value={formData.amountPayable}
                            contentEditable={false}
                            sx={textFieldStyles}
                            InputProps={{
                                readOnly: true,
                            }}
                        />
                        <TextField
                            label="Amount Received"
                            name="paidAmount"
                            value={formData.paidAmount}
                            onChange={handleChange}
                            sx={textFieldStyles}
                        />
                        <Autocomplete
                            options={paymentModes}
                            value={formData.modeOfPayment}
                            onChange={(e, newValue) => {
                                setFormData(prev => ({ ...prev, modeOfPayment: newValue || "" }));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Mode of Payment"
                                    sx={autocompleteStyles}
                                />
                            )}
                            popupIcon={<KeyboardArrowDownIcon />}
                        />
                        <TextField
                            label="Remarks"
                            name="remarks"
                            value={formData.remarks}
                            onChange={handleChange}
                            multiline
                            rows={2}
                            sx={{
                                ...textFieldStyles,
                                "& .MuiInputBase-root": {
                                    ...textFieldStyles["& .MuiInputBase-root"],
                                    height: 'auto',
                                    p: 1
                                }
                            }}
                        />
                    </Box>

                    {/* Commission Details */}
                    <h3 style={{ fontFamily: 'Roboto Slab', fontSize: '20px', fontWeight: 500, marginBottom: '8px', marginTop: '16px' }}>Commission Details</h3>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px' }}>
                        {policy?.policyCategory === 'motor' ? (
                            <>
                                {policy.policyType !== "Liability" && (
                                    <>
                                        <TextField
                                            label="OD Commission Rate (%)"
                                            name="odCommissionRate"
                                            value={formData.odCommissionRate}
                                            onChange={handleChange}
                                            type="number"
                                            sx={textFieldStyles}
                                            inputProps={{ step: "1", min: "0", max: "100" }}
                                        />
                                        <TextField
                                            label="OD Commission Amount"
                                            name="odCommissionAmount"
                                            value={formData.odCommissionAmount}
                                            onChange={handleChange}
                                            sx={textFieldStyles}
                                        />
                                    </>
                                )}
                                {(policy.policyType === "Package" || policy.policyType === "Liability") && (
                                    <>
                                        <TextField
                                            label="TP Commission Rate (%)"
                                            name="tpCommissionRate"
                                            value={formData.tpCommissionRate}
                                            onChange={handleChange}
                                            type="number"
                                            sx={textFieldStyles}
                                            inputProps={{ step: "1", min: "0", max: "100" }}
                                        />
                                        <TextField
                                            label="TP Commission Amount"
                                            name="tpCommissionAmount"
                                            value={formData.tpCommissionAmount}
                                            onChange={handleChange}
                                            sx={textFieldStyles}
                                        />
                                    </>
                                )}
                            </>
                        ) : (
                            <TextField
                                label="Commission Rate (%)"
                                name="commissionRate"
                                value={formData.commissionRate}
                                onChange={handleChange}
                                sx={textFieldStyles}
                            />
                        )}
                        <TextField
                            label="TDS Rate (%)"
                            name="tdsRate"
                            type="number"
                            value={formData.tdsRate}
                            onChange={handleChange}
                            sx={textFieldStyles}
                        />
                        <TextField
                            label="Total Commission"
                            name="totalCommission"
                            value={formData.totalCommission}
                            onChange={handleChange}
                            sx={{ ...textFieldStyles, "& .MuiInputBase-input": { ...textFieldStyles["& .MuiInputBase-input"], color: '#166534', fontWeight: 'bold' } }}
                            inputProps={{
                                inputMode: "decimal"
                            }}
                        />
                    </Box>
                </div>

                {/* Footer Actions */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: "12px", mt: 2 }}>
                    <Button
                        onClick={onClose}
                        variant="outlined"
                        sx={{
                            width: "149px",
                            height: "60px",
                            px: "12px",
                            py: "16px",
                            borderRadius: "8px",
                            borderColor: "#B00020",
                            color: "#B00020",
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "18px",
                            lineHeight: "22px",
                            letterSpacing: "0.01em",
                            textTransform: "none",
                            "&:hover": {
                                backgroundColor: "inherit",
                                color: "#B00020",
                                boxShadow: 10
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        sx={{
                            width: "149px",
                            height: "60px",
                            px: "12px",
                            py: "16px",
                            borderRadius: "8px",
                            backgroundColor: isSubmitting ? "#7e9ed8" : "#0063F7 !important",
                            color: "#FFFFFF",
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "18px",
                            lineHeight: "22px",
                            letterSpacing: "0.5px",
                            textTransform: "none",
                            whiteSpace: "nowrap",
                            position: "relative",
                            transition: "all 0.3s ease",
                            "&:hover:not(:disabled)": {
                                backgroundColor: "#0051d4 !important",
                                transform: "translateY(-1px)",
                                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                            },
                            "&:active:not(:disabled)": {
                                transform: "translateY(0)"
                            },
                            "&:disabled": {
                                cursor: "not-allowed",
                                backgroundColor: "#7e9ed8 !important"
                            }
                        }}
                    >
                        {isSubmitting ? "Updating..." : "Update"}
                    </Button>
                </Box>

            </div>
            <style>{`
        .unified-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1300;
        }
      `}</style>
        </div >
    );
};

export default CommissionEditDialog;
